import { useMemo, useRef } from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import moment from "moment";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { useAppSelector } from "redux/hooks";
import LineItemExpander from "./LineItemExpander";
import TitleLink from "components/core/basic/TitleLink";
import { useLocation } from "react-router-dom";
import { Badge } from "react-bootstrap";
import { getDSPMetricSpec } from "./metrics";
import { GOAL_KPI_LOOKUP } from "./constants";
import { css } from "../../../styled-system/css";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const DSPLineItemsTable = ({ data, isLoading, campaignData = {} }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { dspAdvertiser } = useDSPSellerInfo();
  const tableRef = useRef();
  const TABLE_ID = "dspLineItemsTable";
  const filterProps = useCustomTableFilters(TABLE_ID);

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);

  const formattedData = useMemo(() => {
    return data?.map((row) => {
      const formattedState = row.delivery_status === "ENDED" ? "ENDED" : row.state;

      return { ...row, state: formattedState };
    });
  }, [data]);
  const columnSpecs = useMemo(
    () => [
      {
        key: "state",
        compareKey: null,
        options: {
          metric: {
            id: "state",
            format: "text",
            accessor: (d) => d,
            name: "State",
          },
          formatter: (v) => {
            const isEnded = v.delivery_status === "ENDED";
            return <span>{isEnded ? "Ended" : toTitleCase(v.state)}</span>;
          },
          toolTip: `The campaign's state`,
          hideFilter: true,
        },
      },
      {
        key: "delivery_status",
        compareKey: null,
        options: {
          hideFilter: true,
          formatter: (value) => toTitleCase(snakeCaseToSentenceCase(value)),
          metric: {
            name: "Delivery Status",
            toolTip: `The campaign's delivery status`,
          },
          checked: false,
        },
      },
      {
        key: "start_date_time",
        compareKey: null,
        options: {
          metric: {
            id: "start_date_time",
            format: "date",
            accessor: (d) => (d?.start_date_time ? moment(d?.start_date_time).format("ll") : "-"),
            name: "Start Date",
          },
          toolTip: `The campaign's start date`,
        },
      },
      {
        key: "end_date_time",
        compareKey: null,
        options: {
          metric: {
            id: "end_date_time",
            format: "date",
            accessor: (d) => (d?.end_date_time ? moment(d?.end_date_time).format("ll") : "-"),
            name: "End Date",
          },
          toolTip: `The campaign's end date`,
        },
      },

      {
        key: "dsp_base_bid",
        compareKey: null,
        options: { checked: false },
      },
      {
        key: "creation_date_time",
        compareKey: null,
        options: {
          metric: {
            id: "creation_date_time",
            format: "date",
            accessor: (d) => (d?.creation_date_time ? moment(d?.creation_date_time).format("ll") : "-"),
            name: "Created On",
            options: {
              checked: false,
            },
          },
          toolTip: `The campaign's creation date`,
        },
      },
      {
        key: "dsp_frequencies",
        compareKey: null,
        options: {
          metric: {
            id: "dsp_frequencies",
            format: "text",
            accessor: (d) => <span>{d.frequencies.map((config) => toTitleCase(config.frequencyType))}</span>,
            name: "Frequency",
            options: {
              checked: false,
            },
          },
          toolTip: `A set limit on the number of times an advertisement can be served to a unique user during a specific time period.`,
        },
      },
      {
        key: "dsp_rotation_type",
        compareKey: null,
        options: {
          metric: {
            id: "dsp_rotation_type",
            format: "text",
            accessor: (d) => <span>{toTitleCase(d.creative_rotation_type)}</span>,
            name: "Creative Rotation Type",
            options: {
              checked: false,
            },
          },
          toolTip: `The creative's rotation type.`,
        },
      },
      {
        key: "goal",
        compareKey: null,
        options: {
          checked: false,
          metric: {
            id: "goal",
            format: "text",
            accessor: (d) => d,
            name: "Goal",
          },
          width: 160,
          formatter: (v) => {
            // For now only showing goal if it's a running order, otherwise we don't have accurate data to compare against the goal
            const showGoalCompare = v.state === "ACTIVE" && v.delivery_status === "DELIVERING";

            const goalSpec = GOAL_KPI_LOOKUP[campaignData.optimization?.goalSetting.kpi];
            if (!goalSpec) return;

            let goalTarget = campaignData.optimization?.goalSetting?.targetKpi;

            if (goalSpec.format === "percent") {
              goalTarget /= 100;
            }

            const spec = getDSPMetricSpec(marketPlace, goalSpec.accessor);
            const onTarget = goalSpec.lower ? v[goalSpec.accessor] < goalTarget : v[goalSpec.accessor] > goalTarget;

            return (
              <span>
                <small>
                  Actual {goalSpec.label}: {showGoalCompare ? spec.formatter(v[goalSpec.accessor]) : "-"}
                </small>
                <br />
                <small>
                  {showGoalCompare ?
                    <>
                      {onTarget ?
                        <span className={css({ color: "trellisPalette.12", fontWeight: "bold" })}>On Target</span>
                      : <span className={css({ color: "trellisPalette.17", fontWeight: "bold" })}>Off Target</span>}
                    </>
                  : null}{" "}
                  (vs. Goal: {spec.formatter(goalTarget)})
                </small>
              </span>
            );
          },
        },
      },
      { key: "dsp_sales", compareKey: "dsp_sales_percent" },
      { key: "dsp_total_sales", compareKey: "dsp_total_sales_percent" },
      { key: "dsp_ecpm", compareKey: "dsp_ecpm_percent" },
      { key: "dsp_impressions", compareKey: "dsp_impressions_percent" },
      { key: "dsp_viewable_impressions", compareKey: "dsp_viewable_impressions_percent" },
      { key: "dsp_viewability", compareKey: "dsp_viewability_delta" },
      { key: "dsp_clicks", compareKey: "dsp_clicks_percent" },
      { key: "dsp_cost", compareKey: "dsp_cost_percent" },
      { key: "dsp_total_purchases", compareKey: "dsp_total_purchases_percent" },
      { key: "dsp_units", compareKey: "dsp_units_percent" },
      { key: "dsp_total_units", compareKey: "dsp_total_units_percent", options: { checked: false } },
      { key: "dsp_ctr", compareKey: "dsp_ctr_delta", options: { checked: false } },
      {
        key: "dsp_total_acos",
        compareKey: "dsp_total_acos_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_roas",
        compareKey: "dsp_total_roas_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_views",
        compareKey: "dsp_total_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_detail_page_view_rate",
        compareKey: "dsp_total_detail_page_view_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_add_to_cart",
        compareKey: "dsp_total_add_to_cart_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views",
        compareKey: "dsp_new_to_brand_detail_page_views_percent",
        options: { checked: false },
      },
      {
        key: "dsp_new_to_brand_detail_page_views_rate",
        compareKey: "dsp_new_to_brand_detail_page_views_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpvs",
        compareKey: "dsp_total_new_to_brand_dpvs_percent",
        options: { checked: false },
      },
      {
        key: "dsp_total_new_to_brand_dpv_rate",
        compareKey: "dsp_total_new_to_brand_dpv_rate_delta",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches",
        compareKey: "dsp_brand_searches_percent",
        options: { checked: false },
      },
      {
        key: "dsp_brand_searches_rate",
        compareKey: "dsp_brand_searches_rate_delta",
        options: { checked: false },
      },
    ],
    [campaignData.optimization?.goalSetting.kpi, campaignData.optimization?.goalSetting?.targetKpi, marketPlace],
  );

  return (
    <Table
      marketPlace={marketPlace}
      tableRef={tableRef}
      tableId={TABLE_ID}
      isExpandable={true}
      isSelectable={false}
      titleCol={{
        id: "line_item",
        isStatic: true,
        Header: "Line Item",
        accessor: "text",
        format: "text",
        style: { width: "300px" },
        width: 300,
        Cell: ({ row }) => {
          const { name: dspLineItemName, ad_group_id: lineItemId, program_name: programName } = row?._original ?? {};

          let lineItemDetailsURL = `/user/dsp/${dspAdvertiser?.id}/lineitems/${lineItemId}`;

          if (urlParams.toString()) {
            lineItemDetailsURL = `${lineItemDetailsURL}?${urlParams.toString()}`;
          }

          const tags = [];
          if (programName) {
            tags.push(programName);
          }

          return (
            <>
              <TitleLink
                to={lineItemDetailsURL}
                style={{
                  textDecoration: "none",
                  fontWeight: "500",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                }}
                title={dspLineItemName}
              />
              <div>
                {tags.map((tag) => (
                  <Badge key={tag} className="badge-primary" pill>
                    {tag}
                  </Badge>
                ))}
              </div>
            </>
          );
        },
        Filter: () => null,
      }}
      data={formattedData}
      columnSpecs={columnSpecs}
      getSearchCriteria={(row) => {
        const { name: dspLineItemName } = row ?? {};
        return `${dspLineItemName}`;
      }}
      tableRowDescription={"Campaigns"}
      defaultSorted={[{ id: "dsp_sales", desc: true }]}
      isLoading={isLoading}
      filterWidget={true}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      {...filterProps}
      handleBulkChanges={() => {}}
      minRows={1}
      selectionKey={"id"}
      ExpandedContent={({ row }) => {
        return <LineItemExpander row={row.row} />;
      }}
    />
  );
};

export default DSPLineItemsTable;
